import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { Snackbar } from "@material-ui/core";
import { SnackbarContent } from "../../styled-components/shared";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import { Modal } from "../common/Modal";
import { createPrimaryItem, createGhostItem } from "../common/modalItems";
import { AuthContext } from "../../authentication/Auth";
import "@zebra-fed/icons/Alert/index.css";
import { InputField } from "../common/InputField";
import { DropdownField } from "../common/DropdownField";

import app from "../../authentication/firebaseStart";

const firestore = app.firestore();

const useStyles = makeStyles(() => ({
  scrollableContent: {
    maxHeight: "calc(100vh - 200px)",
    overflowY: "auto",
  },
  mainContent: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  sectionHeader: {
    fontSize: 14,
    fontWeight: 400,
    margin: 0,
    color: "#636e72",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  row: {
    display: "flex",
    gap: 8,
  },
  siteName: {
    overflow: "auto",
    zIndex: 9999,
  },
}));

const defaultDialogProps = {
  width: "640px",
};

function KioskModal({
  modalRef,
  onClose,
  initialData = null
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const context = useContext(AuthContext);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sitesList, setSitesList] = useState([]);

  const getInitialValues = () => {
    return {
      kioskName: initialData?.__name__ || "",
      disabled: true,
      ...initialData,
    };
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    isInitialValid: true,
    onSubmit: (values) => {
      handleSubmit({ ...values });
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { companyID } = context.currentUser.claims;
        const query = firestore
          .collection('tenants')
          .doc(companyID)
          .collection('SiteLocations');

        const snapshot = await query.get();
        const sites = snapshot.docs.map(doc => {
          const siteName = doc.data()?.siteName
          return {
            label: siteName,
            value: siteName
          }
        });
        setSitesList(sites ?? []);
      } catch (error) {
        console.error("Error fetching site locations:", error);
      }
    };
    fetchData();
  }, []);

  async function handleSubmit(values) {
    setIsLoading(true);
    try {
      const companyID = context?.currentUser?.claims?.companyID;
      const documentId = initialData.__name__;
      const kiosk = {
        siteName: values.siteName || ""
      };

      await firestore
        .collection("tenants")
        .doc(companyID)
        .collection("Kiosk")
        .doc(documentId)
        .update({
          ...kiosk,
        });


      setIsLoading(false);
      onClose();
      modalRef.current.toggleDialog();
      formik.resetForm();
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setSnackbarOpen(true);
    }
  }

  const handleCancel = () => {
    modalRef.current.toggleDialog();
    formik.resetForm();
  };

  const isFormValid = () => {
    return formik.isValid && Object.keys(formik.errors).length === 0;
  };

  const getModalActions = () => {
    const actions = [
      createGhostItem({
        onClick: handleCancel,
        disabled: isLoading,
        children: t("cancel"),
      }),
      createPrimaryItem({
        onClick: formik.handleSubmit,
        disabled: isLoading || !isFormValid() || !formik.dirty,
        children: t("update"),
      }),
    ];
    return actions;
  };

  return (
    <>
      <Modal
        modalRef={modalRef}
        actions={getModalActions()}
        dialogProps={{
          ...defaultDialogProps,
          style: { cursor: isLoading ? "wait" : "default" },
        }}
      >
        <div className={classes.scrollableContent}>
          <div className={classes.mainContent}>
            <div className={classes.section}>
              <Typography className={classes.sectionHeader}>
                {t("basicInformation")}
              </Typography>
              <div className={classes.row}>
                <InputField
                  label={t("kioskName")}
                  fullWidth={true}
                  inputProps={{ maxLength: 50 }}
                  disabled={true}
                  {...formik.getFieldProps("kioskName")}
                />
              </div>
              <div className={classes.row}>
                <DropdownField
                  label={"Site"}
                  fullWidth={true}
                  required={true}
                  options={sitesList ?? []}
                  value={formik.values?.siteName || ""}
                  onChange={(event) =>
                    formik.setFieldValue("siteName", event.target.value)
                  }
                  error={
                    formik.touched.siteName &&
                    formik.errors.siteName
                  }
                  helperText={
                    formik.touched.siteName &&
                    formik.errors.siteName
                  }
                  openDropDownInBody={true}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <SnackbarContent error={true}>
          {t("Error updating kisok")}
        </SnackbarContent>
      </Snackbar>
    </>
  );
}

export default KioskModal;
